import Banner from 'src/components/banner/Banner';
import { IconInfo, IconWarning } from '@americanexpress/dls-react';
import LiteralsService from 'src/services/literals/LiteralsService';
import React, { useEffect, useState } from 'react';
import { invalidateSyncStatus, useAccountSyncStatuses } from 'src/hooks/queries/accounts';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { goToSettings } from 'src/services/router.service';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import { DLSSize } from 'src/types/dls-size.enum';
import AccountService from 'src/services/account.service';
import useCurrentUser from 'src/hooks/useCurrentUser';
import { invalidateTransactionsSpending } from 'src/hooks/queries/spending';

const pagesWithoutLink: BudgetPages[] = [BudgetPages.SETTINGS];

export const SyncBanner = (): JSX.Element => {
    const history = useHistory();
    const { pathname } = useLocation();
    const enabled: boolean = pathname !== BudgetPages.INELIGIBLE;
    const shouldShowLink: boolean = !pagesWithoutLink.includes(pathname);

    const queryClient: QueryClient = useQueryClient();
    const { isFullPlanUser } = useCurrentUser();
    const { isLoading: isLoadingSyncStatuses, data: syncStatuses } = useAccountSyncStatuses(enabled);
    const isSyncingAccounts: boolean = AccountService.isSyncingAccounts(syncStatuses);
    const hasSyncError: boolean = AccountService.hasSyncError(syncStatuses);

    const [previousDisplaySyncBanner, setPreviousDisplaySyncBanner] = useState<boolean>(false);

    const displaySyncBanner = enabled && !isLoadingSyncStatuses && (isSyncingAccounts || hasSyncError);

    useEffect(() => {
        let interval: NodeJS.Timer;
        if (displaySyncBanner && isSyncingAccounts) {
            interval = setInterval(() => {
                void invalidateSyncStatus(queryClient);
                // continuously refresh transactions and spending data while syncing
                void invalidateTransactionsSpending(queryClient);
            }, 5000);
            setPreviousDisplaySyncBanner(true);
        } else if (previousDisplaySyncBanner) {
            void queryClient.invalidateQueries();
            setPreviousDisplaySyncBanner(false);
        }
        return () => clearInterval(interval);
    }, [displaySyncBanner, isSyncingAccounts]);

    if (!displaySyncBanner) return null;

    const handleLinkClick = (): void => goToSettings(isFullPlanUser, history);

    return isSyncingAccounts ? (
        <Banner
            icon={<IconInfo />}
            boldText={LiteralsService.getLiteral('AccountSyncBanner.linkingInProgress')}
            text={LiteralsService.getLiteral('AccountSyncBanner.yourSpendingInformation')}
            data-cy={Selectors.General.SYNC_BANNER}
        />
    ) : (
        <Banner
            icon={<IconWarning size={DLSSize.MEDIUM} />}
            boldText={LiteralsService.getLiteral('AccountSyncBanner.unableToCompleteLinking')}
            text={`${LiteralsService.getLiteral('AccountSyncBanner.yourSpendSmartInformation')}
                ${shouldShowLink ? '' : LiteralsService.getLiteral('AccountSyncBanner.pleaseReview')}`}
            data-cy={Selectors.General.SYNC_BANNER}
            variant="error"
            linkText={shouldShowLink && LiteralsService.getLiteral('AccountSyncBanner.tryLinkingAgain')}
            onLinkClick={shouldShowLink && handleLinkClick}
            href={BudgetPages.SETTINGS}
            tag={adobeTags.Banner.AccountSyncError}
        />
    );
};
